import React, { useCallback, useMemo, useState } from 'react';
import useHierarchyCascade, { defaultHierarchySelection } from '../hooks/useHierarchyCascade';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	MenuItem,
	Paper,
	Select,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';

const defaultNameFiltering = {
	search: '',
	...defaultHierarchySelection,
};

const sectionPaperSx = {
	padding: 1.25,
	borderRadius: 1.5,
	borderColor: 'divider',
	backgroundColor: 'grey.50',
};

const sectionTitleSx = {
	marginBottom: 0.75,
};

function cloneNameFiltering(filteringState) {
	return { ...filteringState };
}

export default function SystemNameFilterDialog({ dialogOpen, hierarchy = {}, handleDialogClose }) {
	const [nameFiltering, setNameFiltering] = useState(() => cloneNameFiltering(defaultNameFiltering));
	const [lastAppliedNameFiltering, setLastAppliedNameFiltering] = useState(() => cloneNameFiltering(defaultNameFiltering));
	
	const hierarchySelection = useMemo(() => ({
		organization: nameFiltering.organization,
		region: nameFiltering.region,
		facility: nameFiltering.facility,
		building: nameFiltering.building,
	}), [nameFiltering.organization, nameFiltering.region, nameFiltering.facility, nameFiltering.building]);

	const handleHierarchySelectionChange = useCallback((nextSelection) => {
		setNameFiltering((prevFiltering) => ({
			...prevFiltering,
			...nextSelection,
		}));
	}, []);

	const {
		organizationOptions,
		regionOptions,
		facilityOptions,
		buildingOptions,
		setOrganization,
		setRegion,
		setFacility,
		setBuilding,
	} = useHierarchyCascade({
		hierarchy,
		selection: hierarchySelection,
		onSelectionChange: handleHierarchySelectionChange,
	});

	const handleApply = () => {
		const nextFiltering = cloneNameFiltering(nameFiltering);
		setLastAppliedNameFiltering(nextFiltering);
		handleDialogClose(nextFiltering);
	};

	const handleCancel = () => {
		const restoredFiltering = cloneNameFiltering(lastAppliedNameFiltering);
		setNameFiltering(restoredFiltering);
		handleDialogClose(restoredFiltering);
	};

	const handleReset = () => {
		setNameFiltering(cloneNameFiltering(defaultNameFiltering));
	};

	const setNameFilter = (key, value) => {
		setNameFiltering((prevFiltering) => ({
			...prevFiltering,
			[key]: value,
		}));
	};

	return (
		<Dialog
			open={dialogOpen}
			onClose={handleCancel}
			fullWidth
			maxWidth="xs"
			PaperProps={{
				sx: {
					borderRadius: 1.5,
				},
			}}
		>
			<DialogTitle sx={{ paddingX: 2, paddingY: 1.5 }}>
				<Box display="flex" justifyContent="space-between" alignItems="flex-start">
					<Typography variant="h6" fontFamily="Saira,sans-serif" fontWeight="600">
						Location Filters
					</Typography>
					<IconButton edge="end" color="inherit" onClick={handleCancel} aria-label="close">
						<CloseIcon />
					</IconButton>
				</Box>
			</DialogTitle>
			<DialogContent sx={{ paddingTop: 0.5, paddingX: 2, paddingBottom: 1 }}>
				<Paper variant="outlined" sx={sectionPaperSx}>
					<Typography fontFamily="Saira,sans-serif" fontWeight="600" sx={sectionTitleSx}>
						Name / Serial Number
					</Typography>
					<Grid container spacing={0.75} alignItems="center">
						<Grid item xs>
							<TextField
								value={nameFiltering.search}
								onChange={(event) => setNameFilter('search', event.target.value)}
								label="Search"
								placeholder="Location name or serial number"
								size="small"
								fullWidth
							/>
						</Grid>
						<Grid item>
							<Tooltip title="Clear search text">
								<span>
									<Button
										variant="outlined"
										size="small"
										onClick={() => setNameFilter('search', '')}
										disabled={nameFiltering.search === ''}
										sx={{ minWidth: 0, width: 32, height: 32, padding: 0 }}
									>
										<ClearIcon fontSize="small" />
									</Button>
								</span>
							</Tooltip>
						</Grid>
					</Grid>
				</Paper>

				<Paper variant="outlined" sx={{ marginTop: 1, ...sectionPaperSx }}>
					<Typography fontFamily="Saira,sans-serif" fontWeight="600" sx={sectionTitleSx}>
						Organizational Filters
					</Typography>
					<Grid container spacing={0.75}>
						<Grid item xs={12}>
							<Select
								value={nameFiltering.organization}
								onChange={(event) => setOrganization(event.target.value)}
								displayEmpty
								size="small"
								fullWidth
							>
								<MenuItem value=""><em>All Organizations</em></MenuItem>
								{organizationOptions.map((option) => (
									<MenuItem key={option} value={option}>{option}</MenuItem>
								))}
							</Select>
						</Grid>
						<Grid item xs={12}>
							<Select
								value={nameFiltering.region}
								onChange={(event) => setRegion(event.target.value)}
								displayEmpty
								size="small"
								fullWidth
								disabled={!nameFiltering.organization}
							>
								<MenuItem value=""><em>All Regions</em></MenuItem>
								{regionOptions.map((option) => (
									<MenuItem key={option} value={option}>{option}</MenuItem>
								))}
							</Select>
						</Grid>
						<Grid item xs={12}>
							<Select
								value={nameFiltering.facility}
								onChange={(event) => setFacility(event.target.value)}
								displayEmpty
								size="small"
								fullWidth
								disabled={!nameFiltering.region}
							>
								<MenuItem value=""><em>All Facilities</em></MenuItem>
								{facilityOptions.map((option) => (
									<MenuItem key={option} value={option}>{option}</MenuItem>
								))}
							</Select>
						</Grid>
						<Grid item xs={12}>
							<Select
								value={nameFiltering.building}
								onChange={(event) => setBuilding(event.target.value)}
								displayEmpty
								size="small"
								fullWidth
								disabled={!nameFiltering.facility}
							>
								<MenuItem value=""><em>All Buildings</em></MenuItem>
								{buildingOptions.map((option) => (
									<MenuItem key={option} value={option}>{option}</MenuItem>
								))}
							</Select>
						</Grid>
					</Grid>
				</Paper>
			</DialogContent>
			<DialogActions sx={{ justifyContent: 'space-between', paddingX: 2, paddingY: 1.5 }}>
				<Button onClick={handleReset} variant="outlined" color="primary" sx={{ fontWeight: 600 }}>
					Reset
				</Button>
				<Box>
					<Button onClick={handleCancel} variant="outlined" sx={{ marginRight: 1 }}>
						Cancel
					</Button>
					<Button onClick={handleApply} variant="contained">
						Apply
					</Button>
				</Box>
			</DialogActions>
		</Dialog>
	);
}